/**
 * A map's shape for the 3D viewer: the walls, floors and boxes players collide
 * with (Valve's physics mesh), cut to the playable area and simplified, with no
 * textures. Made on an admin's computer by tools/map-export from the game files,
 * uploaded once per map update, and kept private: the meshes are Valve's.
 *
 * File (`<map>.mesh.bin`, gzipped for the wire), little-endian:
 *   "69LM"            4 bytes
 *   header length     u32
 *   header            JSON (MapMeshHeader), padded with spaces to a multiple of 4
 *   positions         u16 × 3 per vertex, quantised between header.min and header.max
 *   (2 bytes padding when the vertex count is odd)
 *   indices           u16 or u32 (header.index) × 3 per triangle, grouped by surface
 *
 * Coordinates are CS2 world units, Z up, as in demos.
 */

export const MESH_MAGIC = "69LM";
export const MESH_FORMAT = 1;

/** How a surface is drawn: walls and floors, see-through glass, or fences and grates bullets go through. */
export type SurfaceKind = "solid" | "glass" | "fence";

export interface MeshSurface {
  /** Valve's surface property: "concrete", "wood_plank", "metal"… */
  name: string;
  kind: SurfaceKind;
  /** First index (not triangle) of the surface's triangles. */
  start: number;
  /** Number of indices. */
  count: number;
}

export interface MapMeshHeader {
  format: number;
  map: string;
  /** Game build the mesh comes from, as the export reports it (file date of the map). */
  source: string;
  min: [number, number, number];
  max: [number, number, number];
  vertices: number;
  triangles: number;
  index: 16 | 32;
  surfaces: MeshSurface[];
  /** Heights (Z) where players stand most, low to high: the floors a roof cut snaps to. */
  levels: number[];
}

export interface MapMesh extends MapMeshHeader {
  /** x, y, z per vertex, in world units. */
  positions: Float32Array;
  indices: Uint32Array;
}

const Q = 65535;

/** Writes a mesh (not gzipped). Positions are rounded to 1/65535 of the box. */
export function encodeMapMesh(mesh: MapMesh): Uint8Array {
  const { positions, indices, ...rest } = mesh;
  const vertices = positions.length / 3;
  const index: 16 | 32 = vertices <= 65536 ? 16 : 32;
  const header: MapMeshHeader = { ...rest, format: MESH_FORMAT, vertices, triangles: indices.length / 3, index };
  let json = JSON.stringify(header);
  json += " ".repeat((4 - (new TextEncoder().encode(json).length % 4)) % 4);
  const head = new TextEncoder().encode(json);
  const posBytes = vertices * 6 + (vertices % 2 ? 2 : 0);
  const out = new Uint8Array(8 + head.length + posBytes + indices.length * (index / 8));
  out.set(new TextEncoder().encode(MESH_MAGIC), 0);
  const view = new DataView(out.buffer);
  view.setUint32(4, head.length, true);
  out.set(head, 8);
  let at = 8 + head.length;
  const [x0, y0, z0] = header.min;
  const span = header.max.map((m, k) => m - header.min[k] || 1);
  for (let v = 0; v < vertices; v++)
    for (let k = 0; k < 3; k++) {
      const t = (positions[v * 3 + k] - [x0, y0, z0][k]) / span[k];
      view.setUint16(at, Math.max(0, Math.min(Q, Math.round(t * Q))), true);
      at += 2;
    }
  at = 8 + head.length + posBytes;
  for (const i of indices) {
    if (index === 16) view.setUint16(at, i, true);
    else view.setUint32(at, i, true);
    at += index / 8;
  }
  return out;
}

export class MeshFormatError extends Error {}

/** Reads a mesh file (already unzipped); throws MeshFormatError on anything else. */
export function decodeMapMesh(bytes: Uint8Array): MapMesh {
  if (bytes.length < 8 || new TextDecoder().decode(bytes.subarray(0, 4)) !== MESH_MAGIC) throw new MeshFormatError("Not a 69LY map mesh.");
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const headLength = view.getUint32(4, true);
  if (8 + headLength > bytes.length) throw new MeshFormatError("The mesh file is cut short.");
  let header: MapMeshHeader;
  try {
    header = JSON.parse(new TextDecoder().decode(bytes.subarray(8, 8 + headLength))) as MapMeshHeader;
  } catch {
    throw new MeshFormatError("The mesh header is not readable.");
  }
  if (header.format !== MESH_FORMAT) throw new MeshFormatError(`Mesh format ${header.format} is not supported (expected ${MESH_FORMAT}).`);
  const { vertices, triangles, index } = header;
  const posBytes = vertices * 6 + (vertices % 2 ? 2 : 0);
  const need = 8 + headLength + posBytes + triangles * 3 * (index / 8);
  if (bytes.length !== need) throw new MeshFormatError("The mesh file has the wrong size.");
  const positions = new Float32Array(vertices * 3);
  const span = header.max.map((m, k) => m - header.min[k] || 1);
  let at = 8 + headLength;
  for (let v = 0; v < vertices; v++)
    for (let k = 0; k < 3; k++) {
      positions[v * 3 + k] = header.min[k] + (view.getUint16(at, true) / Q) * span[k];
      at += 2;
    }
  at = 8 + headLength + posBytes;
  const indices = new Uint32Array(triangles * 3);
  for (let i = 0; i < indices.length; i++) {
    const value = index === 16 ? view.getUint16(at, true) : view.getUint32(at, true);
    if (value >= vertices) throw new MeshFormatError("The mesh has a bad index.");
    indices[i] = value;
    at += index / 8;
  }
  return { ...header, positions, indices };
}
